import { memo, useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { DynamicBadge, ActionArea } from 'domains/ContentProcessing/AvcInstances/Instance/Tasks/Task'
import { useAvcInstanceTaskUpdateMutation } from 'hooks/mutations/content'
import {
  alertsRoute,
  rawRoute,
  avcInstanceTaskRoute,
  statusRoute,
} from 'pages/NWPlatform/AvcSystem/Instances/Instance/Task'
import { avcInstanceTaskQueryKeys } from 'services/queryKeys'
import { RefetchIntervals } from 'types/query'

const StructureRaw = () => {
  const { t } = useTranslation(['pages', 'entity'])

  const { avcInstanceId: instanceId, avcInstanceTaskId } = avcInstanceTaskRoute.useParams()

  const avcInstanceTaskQuery = useQuery({
    ...avcInstanceTaskQueryKeys.detail({ instanceId, taskId: avcInstanceTaskId }),
    refetchInterval: RefetchIntervals.NORMAL,
  })

  const { mutate, ...avcInstanceUpdateMutation } = useAvcInstanceTaskUpdateMutation()

  const handleUpdateAvcInstanceTaskName = useCallback(
    (title: string, closeCallback: () => void) => {
      mutate(
        {
          taskId: avcInstanceTaskId,
          instanceId,
          avcInstanceTaskConfigGenericDistributedTaskEdit: { name: title },
        },
        {
          onError: () => enqueueSnackbar(t('pages:ContentProcessing.updateError')),
          onSettled: closeCallback,
        },
      )
    },
    [t, mutate, avcInstanceTaskId, instanceId],
  )

  return (
    <Content
      HeaderSlot={
        <Header
          title={avcInstanceTaskQuery.data?.name ?? ''}
          isLoading={avcInstanceTaskQuery.isLoading}
          isSaving={avcInstanceUpdateMutation.isPending}
          onSave={handleUpdateAvcInstanceTaskName}
          appendix={
            avcInstanceTaskQuery.isSuccess && (
              <DynamicBadge state={avcInstanceTaskQuery.data.state} health={avcInstanceTaskQuery.data.health} />
            )
          }
          Actions={<ActionArea />}
        />
      }
      SubNavigation={
        <Tabs>
          <NavigationTabLink from={avcInstanceTaskRoute.fullPath} to={statusRoute.to}>
            {t('pages:ContentProcessing.status')}
          </NavigationTabLink>
          <NavigationTabLink from={avcInstanceTaskRoute.fullPath} to={rawRoute.to}>
            {t('pages:ContentProcessing.config')}
          </NavigationTabLink>
          <NavigationTabLink from={avcInstanceTaskRoute.fullPath} to={alertsRoute.to}>
            {t('pages:ContentProcessing.alerts')}
          </NavigationTabLink>
        </Tabs>
      }
    />
  )
}

export const Structure = memo(StructureRaw)
